import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { DialogFooter, DialogFormButtons } from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { trpc } from "~/lib/trpc";
import { isQueryParam } from "~/routes/(app)/pages/$pageId/variants/$variantId/-utils/traffic-source-validation";

interface QueryParamFormProps {
  pageId: string;
  variantId: string;
  onBack: () => void;
  onCancel: () => void;
  onSuccess: () => void;
}

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  rule: z.string().min(1, "Rule is required").refine(isQueryParam, {
    message:
      "Invalid query parameter format. Expected format: ?param=value or &param=value",
  }),
});

type FormData = z.infer<typeof FormSchema>;

export function QueryParamForm({
  pageId,
  variantId,
  onBack,
  onCancel,
  onSuccess,
}: QueryParamFormProps) {
  const queryClient = useQueryClient();
  const form = useForm<FormData>({
    defaultValues: {
      rule: "",
      name: "",
    },
    resolver: zodResolver(FormSchema),
  });

  const createRuleMutation = useMutation(
    trpc.variantTrafficSources.addSources.mutationOptions({
      onSuccess: () => {
        toast.success("Traffic source created");
        queryClient.invalidateQueries({
          queryKey: trpc.variantTrafficSources.list.queryKey({
            variantId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.variants.getPaginatedVariants.queryKey({
            pageId,
          }),
        });
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const ruleWatch = form.watch("rule");

  useEffect(() => {
    const nameManuallyChanged = form.formState.touchedFields.name;
    if (!nameManuallyChanged) {
      form.setValue("name", ruleWatch, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [ruleWatch, form]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (data) => {
          await createRuleMutation.mutateAsync({
            pageId,
            variantId,
            rules: [
              {
                rule: data.rule,
                name: data.name,
                type: "query-param",
              },
            ],
          });
          form.reset();
        })}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="rule"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Query Parameter</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  {...field}
                  placeholder="utm_source=email-campaign"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <DialogFormButtons
            isPending={createRuleMutation.isPending}
            onCancel={onBack}
            submitText="Create"
            cancelText="Back"
          />
        </DialogFooter>
      </form>
    </Form>
  );
}
